import type { LeadWithRelations } from '@/types';
import { FollowUpIndicator } from './FollowUpIndicator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { useCustomerIdsByPhone } from '@/hooks/useCustomers';
import { CustomerLink } from '@/components/customers/CustomerLink';
import { cn } from '@/lib/utils';

interface LeadsTableViewProps {
  leads: LeadWithRelations[];
  onLeadClick: (id: string) => void;
  showMoreButton?: React.ReactNode;
  /** Lead currently open in the quick view sheet — its row gets the selected treatment */
  selectedLeadId?: string | null;
}

function getInitials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase() || '?';
}

export function LeadsTableView({
  leads,
  onLeadClick,
  showMoreButton,
  selectedLeadId,
}: LeadsTableViewProps) {
  const { formatShortDate, formatMoney } = useCompanyLocale();
  // Leads created before the contact-sync triggers have no customer_id; match
  // on phone so the name still links to the customer card.
  const resolveCustomerId = useCustomerIdsByPhone(leads);

  return (
    <>
      <div className="grid grid-cols-11 gap-4 border-b px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[.04em] text-muted-foreground">
        <div>Lead #</div>
        <div className="col-span-2">Customer</div>
        <div>Phone</div>
        <div>Service</div>
        <div>Source</div>
        <div>CSR</div>
        <div>Date</div>
        <div>Follow-up</div>
        <div>Lost reason</div>
        <div className="text-right">Price</div>
      </div>

      <div className="divide-y">
        {leads.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground">
            No leads found
          </div>
        ) : (
          leads.map((lead) => {
            const isSelected = selectedLeadId === lead.id;
            return (
              <div
                key={lead.id}
                className={cn(
                  'grid cursor-pointer grid-cols-11 items-center gap-4 px-4 py-[11px] text-[13px] transition-colors',
                  isSelected ? 'bg-primary/10' : 'hover:bg-muted/50',
                )}
                onClick={() => onLeadClick(lead.id)}
              >
                <div className="text-muted-foreground">
                  {lead.lead_number}
                </div>

                <div className="col-span-2 flex min-w-0 items-center gap-2.5">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">
                    {getInitials(lead.customer_name)}
                  </span>
                  <span className="min-w-0">
                    <CustomerLink
                      customerId={resolveCustomerId(lead)}
                      name={lead.customer_name}
                      className="block max-w-full truncate font-medium"
                    />
                    {lead.customer_suburb && (
                      <span className="block truncate text-[11px] text-muted-foreground">
                        {lead.customer_suburb}
                      </span>
                    )}
                  </span>
                </div>

                <div className="truncate text-muted-foreground tabular-nums">
                  {lead.customer_phone}
                </div>

                <div className="truncate text-muted-foreground">
                  {lead.service_types?.name || '-'}
                </div>

                <div className="truncate text-muted-foreground">
                  {lead.lead_sources?.name || '-'}
                </div>

                <div className="truncate">
                  {lead.csr_profile?.full_name ? (
                    <span className="text-muted-foreground">{lead.csr_profile.full_name}</span>
                  ) : (
                    <span className="italic text-warning">Unassigned</span>
                  )}
                </div>

                <div className="text-muted-foreground tabular-nums">
                  {formatShortDate(lead.created_at)}
                </div>

                <div>
                  <FollowUpIndicator date={lead.next_follow_up_date} variant="compact" />
                </div>

                <div className="truncate text-muted-foreground">
                  {lead.lost_reason ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="block cursor-help truncate">{lead.lost_reason}</span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs">{lead.lost_reason}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : '-'}
                </div>

                <div className="text-right font-semibold tabular-nums">
                  {lead.quoted_price ? formatMoney(lead.quoted_price) : '-'}
                </div>
              </div>
            );
          })
        )}
      </div>
      {showMoreButton}
    </>
  );
}
